import { useEffect, useState, useCallback } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  MonitorPlay,
  RefreshCw,
  PowerOff,
  Eye,
  Play,
  Pause,
  Users2,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Avatar, Tag, Spinner, EmptyState, formatDate } from "@/components/shared";
import { ConfirmDialog } from "@/components/dialogs";

function roomKey(r) {
  return r.room_id || r.match_id;
}
function hostName(h) {
  if (!h) return "—";
  if (typeof h === "string") return h;
  return h.name || h.email || h.user_id || "—";
}
function participantCount(p) {
  if (typeof p === "number") return p;
  if (Array.isArray(p)) return p.length;
  return p ?? 0;
}
function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

function RoomDetailSheet({ roomId, open, onOpenChange, onEnded }) {
  const [room, setRoom] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const load = useCallback(async () => {
    if (!roomId) return;
    setLoading(true);
    try {
      const r = await api.watchRoom(roomId);
      setRoom(r?.room || r);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [roomId]);

  useEffect(() => {
    if (open && roomId) load();
  }, [open, roomId, load]);

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto bg-card" data-testid="watch-room-sheet">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">Watch party</SheetTitle>
          </SheetHeader>
          {loading || !room ? (
            <Spinner />
          ) : (
            <div className="mt-4 space-y-5">
              <div className="rounded-xl overflow-hidden bg-secondary ring-1 ring-border/60">
                {room.thumbnail ? (
                  <img src={room.thumbnail} alt="" className="w-full aspect-video object-cover" onError={(e) => (e.currentTarget.style.display = "none")} />
                ) : null}
                <div className="p-4">
                  <div className="font-medium">{room.video_title || "Untitled video"}</div>
                  <div className="text-xs text-muted-foreground mt-1">Video ID: {room.video_id || "—"}</div>
                  <div className="flex items-center gap-2 mt-3">
                    <Tag tone={room.is_playing ? "green" : "muted"}>
                      {room.is_playing ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
                      {room.is_playing ? "Playing" : "Paused"}
                    </Tag>
                    <Tag tone="blue"><Clock className="h-3 w-3" />{Math.round(room.position_seconds ?? room.position ?? 0)}s</Tag>
                    <Tag tone="blue"><Users2 className="h-3 w-3" />{room.participant_count ?? participantCount(room.participants)}</Tag>
                  </div>
                </div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-2">Match</div>
                <div className="rounded-lg bg-secondary/40 px-3 py-2 text-sm font-mono">{roomKey(room)}</div>
              </div>

              <div>
                <div className="text-sm text-muted-foreground mb-2">Participants (matched users)</div>
                <div className="space-y-2">
                  {(room.members || []).map((m, i) => (
                    <div key={m.user_id || i} className="flex items-center gap-3 rounded-lg bg-secondary/40 px-3 py-2" data-testid={`room-user-${i}`}>
                      <Avatar src={m.photo} name={m.name} size={36} />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">{m.name || m.user_id}</div>
                        <div className="text-[11px] text-muted-foreground font-mono truncate">{m.user_id}</div>
                      </div>
                      <Tag tone={m.in_room ? "green" : "muted"} className="ml-auto">
                        {m.in_room ? "In room" : "Away"}
                      </Tag>
                    </div>
                  ))}
                  {(!room.members || room.members.length === 0) && (
                    <div className="text-sm text-muted-foreground">Host: {room.host_name || hostName(room.host)}</div>
                  )}
                </div>
              </div>

              {room.started_at && (
                <div className="text-xs text-muted-foreground">Started {fmtTime(room.started_at)}</div>
              )}

              <Button variant="destructive" className="w-full" onClick={() => setConfirmEnd(true)} data-testid="detail-force-end">
                <PowerOff className="h-4 w-4" /> Force-end this room
              </Button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmEnd}
        onOpenChange={setConfirmEnd}
        title="Force-end watch party?"
        description="This immediately shuts the room for both users. Use as a safety kill-switch."
        confirmText="Force-end"
        destructive
        onConfirm={async () => {
          await api.endWatchRoom(roomId);
          toast.success("Watch party ended");
          onOpenChange(false);
          onEnded?.();
        }}
      />
    </>
  );
}

export default function WatchParties() {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [endTarget, setEndTarget] = useState(null);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setError("");
    try {
      const res = await api.watchRooms("active");
      setRooms(Array.isArray(res) ? res : res?.rooms || []);
    } catch (e) {
      setError(e.message);
      setRooms([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const forceEnd = async () => {
    try {
      await api.endWatchRoom(roomKey(endTarget));
      toast.success("Watch party ended");
      load({ silent: true });
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div>
      <PageHeader
        title="Watch Parties"
        subtitle="Monitor live watch-party rooms and force-end any that break the rules."
        actions={
          <Button variant="outline" onClick={() => load()} data-testid="refresh-btn">
            <RefreshCw className="h-4 w-4" /> Refresh
          </Button>
        }
      />

      {error && (
        <div className="rounded-xl bg-destructive/15 ring-1 ring-destructive/40 px-4 py-3 text-sm mb-4" data-testid="watch-error">
          {error}
        </div>
      )}

      {loading ? (
        <Spinner />
      ) : rooms.length === 0 && !error ? (
        <EmptyState icon={MonitorPlay} title="No active watch parties" hint="Live rooms will appear here while two matched users watch together." />
      ) : (
        <div className="rounded-2xl bg-card ring-1 ring-border/70 overflow-x-auto">
          <Table data-testid="watch-rooms-table">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border/60">
                <TableHead>Now playing</TableHead>
                <TableHead>Host</TableHead>
                <TableHead>Match</TableHead>
                <TableHead className="text-center">Participants</TableHead>
                <TableHead>Started</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rooms.map((r) => (
                <TableRow key={roomKey(r)} className="border-border/50" data-testid={`watch-room-${roomKey(r)}`}>
                  <TableCell>
                    <div className="flex items-center gap-2 max-w-[280px]">
                      <MonitorPlay className="h-4 w-4 text-primary shrink-0" />
                      <span className="truncate">{r.video_title || "Untitled video"}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{r.host_name || hostName(r.host)}</TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground truncate max-w-[160px]">{roomKey(r)}</TableCell>
                  <TableCell className="text-center">
                    <Tag tone="blue"><Users2 className="h-3 w-3" />{r.participant_count ?? participantCount(r.participants)}</Tag>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">{fmtTime(r.started_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="outline" size="sm" onClick={() => setDetailId(roomKey(r))} data-testid={`view-room-${roomKey(r)}`}>
                        <Eye className="h-4 w-4" /> View
                      </Button>
                      <Button variant="destructive" size="sm" onClick={() => setEndTarget(r)} data-testid={`force-end-${roomKey(r)}`}>
                        <PowerOff className="h-4 w-4" /> Force-End
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <RoomDetailSheet
        roomId={detailId}
        open={!!detailId}
        onOpenChange={(v) => !v && setDetailId(null)}
        onEnded={() => load({ silent: true })}
      />

      <ConfirmDialog
        open={!!endTarget}
        onOpenChange={(v) => !v && setEndTarget(null)}
        title="Force-end watch party?"
        description="This immediately shuts the room for both matched users. Use as a safety kill-switch."
        confirmText="Force-end"
        destructive
        onConfirm={forceEnd}
      />
    </div>
  );
}
